import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Briefcase, Store, GraduationCap, Heart,
  Save, Loader2, CheckCircle, ChevronRight, ChevronDown,
  AlertCircle, Users, Wallet, Clock, TrendingUp, Target
} from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


// Options for each field
const OPTIONS = {
  escolaridade: [
    { value: 'nao_alfabetizado', label: 'Não alfabetizado' },
    { value: 'fundamental_incompleto', label: 'Ensino fundamental incompleto' },
    { value: 'fundamental_completo', label: 'Ensino fundamental completo' },
    { value: 'medio_incompleto', label: 'Ensino médio incompleto' },
    { value: 'medio_completo', label: 'Ensino médio completo' },
    { value: 'superior_incompleto', label: 'Ensino superior incompleto' },
    { value: 'superior_completo', label: 'Ensino superior completo' },
    { value: 'pos_graduacao', label: 'Pós-graduação' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  faixa_etaria: [
    { value: 'ate_17', label: 'Até 17 anos' },
    { value: '18_24', label: '18 a 24 anos' },
    { value: '25_34', label: '25 a 34 anos' },
    { value: '35_44', label: '35 a 44 anos' },
    { value: '45_59', label: '45 a 59 anos' },
    { value: '60_mais', label: '60 anos ou mais' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  genero: [
    { value: 'feminino', label: 'Feminino' },
    { value: 'masculino', label: 'Masculino' },
    { value: 'nao_binario', label: 'Não binário' },
    { value: 'outro', label: 'Outro' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  raca_cor: [
    { value: 'branca', label: 'Branca' },
    { value: 'preta', label: 'Preta' },
    { value: 'parda', label: 'Parda' },
    { value: 'amarela', label: 'Amarela' },
    { value: 'indigena', label: 'Indígena' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  renda_mensal: [
    { value: 'sem_renda', label: 'Ainda não tenho renda' },
    { value: 'ate_500', label: 'Até R$ 500' },
    { value: '501_1500', label: 'R$ 501 a R$ 1.500' },
    { value: '1501_3000', label: 'R$ 1.501 a R$ 3.000' },
    { value: '3001_5000', label: 'R$ 3.001 a R$ 5.000' },
    { value: '5001_10000', label: 'R$ 5.001 a R$ 10.000' },
    { value: 'acima_10000', label: 'Acima de R$ 10.000' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  dependentes_renda: [
    { value: '1', label: 'Só eu' },
    { value: '2', label: '2 pessoas' },
    { value: '3', label: '3 pessoas' },
    { value: '4', label: '4 pessoas' },
    { value: '5_mais', label: '5 ou mais pessoas' },
    { value: 'prefiro_nao_informar', label: 'Prefiro não informar' },
  ],
  tempo_atuacao: [
    { value: 'menos_6_meses', label: 'Menos de 6 meses' },
    { value: '6_meses_1_ano', label: '6 meses a 1 ano' },
    { value: '1_2_anos', label: '1 a 2 anos' },
    { value: '3_5_anos', label: '3 a 5 anos' },
    { value: 'mais_5_anos', label: 'Mais de 5 anos' },
  ],
  pretende_formalizar: [
    { value: 'sim_6_meses', label: 'Sim, nos próximos 6 meses' },
    { value: 'sim_futuro', label: 'Sim, no futuro' },
    { value: 'nao', label: 'Não' },
    { value: 'ja_formalizado', label: 'Já sou formalizado(a)' },
  ],
  dificuldades: [
    { value: 'conseguir_clientes', label: 'Conseguir mais clientes' },
    { value: 'divulgar_negocio', label: 'Divulgar meu negócio' },
    { value: 'organizar_financas', label: 'Organizar as finanças' },
    { value: 'definir_precos', label: 'Definir preços' },
    { value: 'acessar_credito', label: 'Acessar crédito' },
    { value: 'formalizar', label: 'Formalizar / regularizar' },
    { value: 'melhorar_producao', label: 'Melhorar a produção ou atendimento' },
    { value: 'contratar_pessoas', label: 'Contratar pessoas' },
    { value: 'usar_redes_sociais', label: 'Usar internet e redes sociais' },
    { value: 'outra', label: 'Outra' },
  ],
  canal_principal: [
    { value: 'loja_fisica', label: 'Loja física' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'feiras', label: 'Feiras / ambulante' },
    { value: 'indicacao', label: 'Indicação' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'outro', label: 'Outro' },
  ],
  canais_secundarios: [
    { value: 'loja_fisica', label: 'Loja física' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'feiras', label: 'Feiras / ambulante' },
    { value: 'indicacao', label: 'Indicação' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'site_proprio', label: 'Site próprio' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'outro', label: 'Outro' },
  ],
  temas_interesse: [
    { value: 'marketing', label: 'Marketing e divulgação' },
    { value: 'vendas', label: 'Vendas' },
    { value: 'financas', label: 'Finanças' },
    { value: 'precificacao', label: 'Precificação' },
    { value: 'formalizacao', label: 'Formalização' },
    { value: 'credito', label: 'Crédito' },
    { value: 'redes_sociais', label: 'Redes sociais' },
    { value: 'atendimento', label: 'Atendimento ao cliente' },
    { value: 'gestao', label: 'Gestão do negócio' },
    { value: 'licitacoes', label: 'Compras públicas / licitações' },
  ],
};

// Block configuration
const BLOCKS = [
  {
    id: 'sobre_voce',
    title: 'Sobre você',
    icon: User,
    color: '#8b5cf6',
    fields: ['escolaridade', 'faixa_etaria', 'genero', 'raca_cor']
  },
  {
    id: 'renda_trabalho',
    title: 'Sua renda e trabalho',
    icon: Wallet,
    color: '#22c55e',
    fields: ['renda_mensal', 'dependentes_renda']
  },
  {
    id: 'negocio_hoje',
    title: 'Seu negócio hoje',
    icon: Store,
    color: '#f97316',
    fields: ['tempo_atuacao', 'pretende_formalizar', 'dificuldades']
  },
  {
    id: 'como_vende',
    title: 'Como você vende',
    icon: TrendingUp,
    color: '#3b82f6',
    fields: ['canal_principal', 'canais_secundarios']
  },
  {
    id: 'apoio',
    title: 'Apoio que você gostaria de receber',
    icon: GraduationCap,
    color: '#ec4899',
    fields: ['deseja_capacitacao', 'temas_interesse']
  }
];

const FIELD_LABELS = {
  escolaridade: 'Nível de escolaridade',
  faixa_etaria: 'Faixa etária',
  genero: 'Gênero',
  raca_cor: 'Raça/cor',
  renda_mensal: 'Renda média mensal do negócio',
  dependentes_renda: 'Quantas pessoas dependem dessa renda?',
  tempo_atuacao: 'Há quanto tempo você atua nesse negócio?',
  pretende_formalizar: 'Pretende formalizar ou regularizar o negócio?',
  dificuldades: 'Principais dificuldades do negócio hoje',
  canal_principal: 'Principal canal de vendas hoje',
  canais_secundarios: 'Outros canais que você também utiliza',
  deseja_capacitacao: 'Deseja receber capacitações, cursos e oportunidades?',
  temas_interesse: 'Temas de interesse'
};

// Single Select Component
const SingleSelect = ({ field, value, onChange, options }) => (
  <div className="space-y-2">
    {options.map(opt => (
      <label
        key={opt.value}
        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
          value === opt.value
            ? 'border-[var(--primary)] bg-[var(--primary)]/5'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <input
          type="radio"
          name={field}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          className="w-4 h-4 text-[var(--primary)]"
        />
        <span className="text-sm">{opt.label}</span>
      </label>
    ))}
  </div>
);

// Multi Select Component
const MultiSelect = ({ field, values, onChange, options, maxSelections }) => {
  const handleToggle = (optValue) => {
    if (values.includes(optValue)) {
      onChange(values.filter(v => v !== optValue));
    } else {
      if (maxSelections && values.length >= maxSelections) {
        toast.error(`Selecione no máximo ${maxSelections} opções`);
        return;
      }
      onChange([...values, optValue]);
    }
  };

  return (
    <div className="space-y-2">
      {maxSelections && (
        <p className="text-xs text-gray-500 mb-2">
          Selecione até {maxSelections} opções ({values.length}/{maxSelections})
        </p>
      )}
      {options.map(opt => (
        <label
          key={opt.value}
          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
            values.includes(opt.value)
              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <input
            type="checkbox"
            checked={values.includes(opt.value)}
            onChange={() => handleToggle(opt.value)}
            className="w-4 h-4 text-[var(--primary)] rounded"
          />
          <span className="text-sm">{opt.label}</span>
        </label>
      ))}
    </div>
  );
};

// Boolean Select Component
const BooleanSelect = ({ value, onChange }) => (
  <div className="flex gap-3">
    {[
      { value: true, label: 'Sim' },
      { value: false, label: 'Não' }
    ].map(opt => (
      <label
        key={String(opt.value)}
        className={`flex-1 flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
          value === opt.value
            ? 'border-[var(--primary)] bg-[var(--primary)]/5'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <input
          type="radio"
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          className="sr-only"
        />
        <span className="font-medium">{opt.label}</span>
      </label>
    ))}
  </div>
);

// Progress Bar Component
const ProgressBar = ({ percentage }) => (
  <div className="w-full">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-gray-700">Perfil {percentage}% completo</span>
      {percentage === 100 && <CheckCircle className="w-5 h-5 text-green-500" />}
    </div>
    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-500"
        style={{ 
          width: `${percentage}%`,
          background: percentage === 100 
            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
            : 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
        }}
      />
    </div>
  </div>
);

export default function CompleteSeuPerfil() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedBlock, setExpandedBlock] = useState('sobre_voce');
  
  const [perfil, setPerfil] = useState({
    escolaridade: null,
    faixa_etaria: null,
    genero: null,
    raca_cor: null,
    renda_mensal: null,
    dependentes_renda: null,
    tempo_atuacao: null,
    pretende_formalizar: null,
    dificuldades: [],
    canal_principal: null,
    canais_secundarios: [],
    deseja_capacitacao: null,
    temas_interesse: [],
    percentual_preenchido: 0
  });

  const loadPerfil = useCallback(async () => {
    try {
      const res = await api.get('/profile/empreendedor');
      setPerfil(res.data);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/perfil');
      return;
    }
    loadPerfil();
  }, [isAuthenticated, loadPerfil, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/profile/empreendedor', perfil);
      setPerfil(res.data);
      toast.success('Perfil salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPerfil(prev => ({ ...prev, [field]: value }));
  };

  const renderField = (field) => {
    const options = OPTIONS[field];
    const value = perfil[field];
    
    if (field === 'deseja_capacitacao') {
      return (
        <BooleanSelect
          value={value}
          onChange={(v) => updateField(field, v)}
        />
      );
    }
    
    if (field === 'dificuldades') {
      return (
        <MultiSelect
          field={field}
          values={value || []}
          onChange={(v) => updateField(field, v)}
          options={options}
          maxSelections={3}
        />
      );
    }
    
    if (field === 'canais_secundarios' || field === 'temas_interesse') {
      return (
        <MultiSelect
          field={field}
          values={value || []}
          onChange={(v) => updateField(field, v)}
          options={options}
        />
      );
    }
    
    return (
      <SingleSelect
        field={field}
        value={value}
        onChange={(v) => updateField(field, v)}
        options={options}
      />
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--primary)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate('/perfil')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Complete seu Perfil</h1>
              <p className="text-white/80 text-sm">
                Aumente sua visibilidade e ajude a fortalecer ações de apoio
              </p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="bg-white/10 rounded-xl p-4">
            <ProgressBar percentage={perfil.percentual_preenchido} />
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="max-w-3xl mx-auto px-4 -mt-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Por que preencher?</strong> Essas informações ajudam o município a entender 
              melhor o perfil dos empreendedores e criar políticas de apoio mais adequadas.
              Todos os campos são opcionais e você pode editar depois.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {BLOCKS.map((block) => {
          const isExpanded = expandedBlock === block.id;
          const Icon = block.icon;
          
          // Calculate block completion
          const filledFields = block.fields.filter(f => {
            const val = perfil[f];
            return val !== null && val !== undefined && val !== '' && 
                   (Array.isArray(val) ? val.length > 0 : true);
          });
          const blockCompletion = Math.round((filledFields.length / block.fields.length) * 100);
          
          // Special case: hide temas_interesse if deseja_capacitacao is not true
          const shouldShowField = (field) => {
            if (field === 'temas_interesse') {
              return perfil.deseja_capacitacao === true;
            }
            return true;
          };
          
          return (
            <div key={block.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExpandedBlock(isExpanded ? null : block.id)}
                className="w-full px-4 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${block.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: block.color }} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-gray-900">{block.title}</h3>
                  <p className="text-xs text-gray-500">{blockCompletion}% preenchido</p>
                </div>
                {blockCompletion === 100 && (
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                )}
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-6">
                  {block.fields.filter(shouldShowField).map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {FIELD_LABELS[field]}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 bg-[var(--primary)] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[var(--primary-dark)] transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Salvar Perfil
          </button>
        </div>
      </div>
    </div>
  );
}
